import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';

/**
 * お客様の一覧を作る唯一の入口。
 *
 * 以前は reservations（予約）から組み立てていた。
 * 予約を使わない業態では常に空になり、
 * 「顧客がいません」としか出ない画面になっていた。
 *
 * 夜職では「誰の客か」がツケ・ボトルキープ・指名に直結するので、
 * 会計・未収金・ボトルキープに残った名前から作る。
 */

export interface LedgerCustomer {
  id: string;
  displayName: string;
  phoneNumber: string;
  /** 来店回数（会計の件数） */
  visitCount: number;
  /** 累計売上 */
  totalSpent: number;
  firstVisitAt: Date | null;
  lastVisitAt: Date | null;
  /** 未収金（ツケ）の残高 */
  receivableBalance: number;
  receivableCount: number;
  /** キープ中のボトル本数 */
  bottleCount: number;
  /** よく付いた担当（指名の多い順） */
  topStaffName: string | null;
  /** 担当ごとの回数 */
  staffCounts: Record<string, number>;
}

const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  return null;
};

/**
 * 同じ人にまとめる鍵。
 * 空白と「様／さん」だけを落とす。
 * 役職まで落とすと「井上社長」と「井上様」が同じ客になる。
 * 夜職では別人のことがあるので、そこは分けたままにする。
 */
function normalizeName(raw: unknown): string {
  return String(raw ?? '')
    .replace(/\s+/g, '')
    .replace(/(様|さん)+$/, '')
    .trim();
}

/**
 * 画面に出す名前。
 * 「井上社長」はもう敬称が付いているので「様」を足さない。
 * 足すと「井上社長様」になる。
 *
 * 「敬称を落としたかどうか」で判定すると、落とさなかった場合と
 * 元から付いていない場合が区別できない。役職の一覧で見る。
 */
const HONORIFIC_SUFFIX = /(様|さん|社長|部長|専務|会長|課長|店長|先生|マスター|ママ|チーママ)$/;

function displayNameOf(_normalized: string, original: string): string {
  const raw = String(original ?? '').replace(/\s+/g, '').trim();
  if (!raw) return '（名前なし）';
  return HONORIFIC_SUFFIX.test(raw) ? raw : `${raw}様`;
}

export async function fetchCustomersFromLedger(shopId: string): Promise<LedgerCustomer[]> {
  const byShop = (col: string) => query(collection(db, col), where('shopId', '==', shopId));

  const [checkouts, receivables, bottles, sessions] = await Promise.all([
    getDocs(byShop('checkouts')),
    getDocs(byShop('receivables')),
    getDocs(byShop('bottleKeeps')),
    getDocs(byShop('tableSessions')),
  ]);

  const map = new Map<string, LedgerCustomer>();
  const ensure = (name: string): LedgerCustomer => {
    const key = normalizeName(name);
    let c = map.get(key);
    if (!c) {
      c = {
        id: key, displayName: displayNameOf(key, name), phoneNumber: '',
        visitCount: 0, totalSpent: 0,
        firstVisitAt: null, lastVisitAt: null,
        receivableBalance: 0, receivableCount: 0,
        bottleCount: 0, topStaffName: null, staffCounts: {},
      };
      map.set(key, c);
    }
    return c;
  };

  // 卓の記録から「その会計は誰の卓だったか」を引けるようにする
  const sessionByTableDate = new Map<string, any>();
  sessions.docs.forEach((d) => {
    const v: any = d.data();
    if (!v.customerName) return;
    sessionByTableDate.set(`${v.tableId}:${v.businessDate}`, v);
  });

  checkouts.docs.forEach((d) => {
    const v: any = d.data();
    if (v.status === 'cancelled' || v.voided) return;
    const sess = sessionByTableDate.get(`${shopId}_t${v.tableNumber}:${v.businessDate}`);
    const name = sess?.customerName;
    if (!name) return; // 名前が残っていない会計は誰の客か決められない
    const c = ensure(name);
    c.visitCount++;
    c.totalSpent += v.total ?? 0;
    const at = toDate(v.createdAt);
    if (at) {
      if (!c.firstVisitAt || at < c.firstVisitAt) c.firstVisitAt = at;
      if (!c.lastVisitAt || at > c.lastVisitAt) c.lastVisitAt = at;
    }
    const staff = sess?.primaryStaffName;
    if (staff) c.staffCounts[staff] = (c.staffCounts[staff] ?? 0) + 1;
  });

  receivables.docs.forEach((d) => {
    const v: any = d.data();
    if (!v.customerName) return;
    const c = ensure(v.customerName);
    if (v.phoneNumber && !c.phoneNumber) c.phoneNumber = v.phoneNumber;
    if (v.status === 'paid') return;
    c.receivableBalance += v.balance ?? 0;
    c.receivableCount++;
  });

  bottles.docs.forEach((d) => {
    const v: any = d.data();
    if (!v.customerName) return;
    const c = ensure(v.customerName);
    if (v.status === 'consumed' || v.status === 'expired') return;
    c.bottleCount++;
  });

  return Array.from(map.values())
    .map((c) => {
      const entries = Object.entries(c.staffCounts);
      entries.sort((a, b) => b[1] - a[1]);
      return { ...c, topStaffName: entries[0]?.[0] ?? null };
    })
    .sort((a, b) => b.totalSpent - a.totalSpent);
}
